import type { CardDefinition, FormatId, ImportedDeckPayload, PlayerSeat, PlaymatFocalPoint } from './types.ts';

export type MtgColor = 'W' | 'U' | 'B' | 'R' | 'G';

/**
 * Policy configuration per ADR-007 & ADR-012:
 * - Zero external image uploads or arbitrary URLs in v1 (prevents nudity/NSFW/offensive imagery).
 * - Playmats and sleeves are strictly limited to official Scryfall card art from the player's deck / color-identity pool.
 * - Reserved future hook (`allowCuratedGuildMats`) for verified guild-mats/sleeves under strict content policy.
 */
export const CURATED_GUILD_COSMETICS_POLICY = {
  allowExternalUploads: false,
  allowCuratedGuildMats: false,
  requireDeckIdentityCardArt: true,
} as const;

export function normalizeFocalPoint(val: unknown): PlaymatFocalPoint {
  if (val === 'top' || val === 'center' || val === 'bottom') return val;
  return 'center';
}

export function focalPointToCssPosition(focalPoint?: PlaymatFocalPoint | null): string {
  if (focalPoint === 'top') return 'center 22%';
  if (focalPoint === 'bottom') return 'center 78%';
  return 'center 50%';
}

export interface CosmeticArtOption {
  cardName: string;
  scryfallId: string;
  artCropUrl: string;
  colors: string[];
  isCommander: boolean;
}

export interface SeatPaletteVariant {
  id: string;
  label: string;
  accentHex: string;
  matBgCss: string;
  matBorderCss: string;
}

const WUBRG_ORDER: MtgColor[] = ['W', 'U', 'B', 'R', 'G'];

export function scryfallArtCropUrl(cardName: string): string {
  return `https://api.scryfall.com/cards/named?format=image&version=art_crop&exact=${encodeURIComponent(cardName)}`;
}

/**
 * Curated format-legal iconic basic/land art options per color identity (always available alongside cards in the deck
 * as long as their color identity is a subset of the deck's color identity).
 */
const IDENTITY_LAND_ART_POOL: { name: string; scryfallId: string; colors: MtgColor[] }[] = [
  { name: 'Command Tower', scryfallId: 'command-tower-art', colors: [] },
  { name: 'Reliquary Tower', scryfallId: 'reliquary-tower-art', colors: [] },
  { name: 'Plains', scryfallId: 'plains-art', colors: ['W'] },
  { name: 'Island', scryfallId: 'island-art', colors: ['U'] },
  { name: 'Swamp', scryfallId: 'swamp-art', colors: ['B'] },
  { name: 'Mountain', scryfallId: 'mountain-art', colors: ['R'] },
  { name: 'Forest', scryfallId: 'forest-art', colors: ['G'] },
];

/**
 * Distinct palette variants keyed by canonical WUBRG color-identity signature.
 * Each identity provides multiple distinct hue/lightness variants so 2–8 players sharing the SAME
 * deck color identity (e.g. four Mono-Blue or three Rakdos decks) remain effortlessly distinguishable!
 */
const IDENTITY_PALETTES: Record<string, SeatPaletteVariant[]> = {
  W: [
    {
      id: 'w_gold',
      label: 'Sunlit Ivory',
      accentHex: '#facc15',
      matBgCss: 'radial-gradient(ellipse at center, #3b3319 0%, #211c0d 62%, #120f07 100%)',
      matBorderCss: 'rgba(250, 204, 21, 0.45)',
    },
    {
      id: 'w_pearl',
      label: 'Alabaster Dawn',
      accentHex: '#fde68a',
      matBgCss: 'radial-gradient(ellipse at center, #38352b 0%, #1e1d17 62%, #10100c 100%)',
      matBorderCss: 'rgba(253, 230, 138, 0.45)',
    },
    {
      id: 'w_amber',
      label: 'Serra Amber',
      accentHex: '#f59e0b',
      matBgCss: 'radial-gradient(ellipse at center, #42290e 0%, #241607 62%, #130b04 100%)',
      matBorderCss: 'rgba(245, 158, 11, 0.45)',
    },
  ],
  U: [
    {
      id: 'u_sapphire',
      label: 'Sapphire Tide',
      accentHex: '#38bdf8',
      matBgCss: 'radial-gradient(ellipse at center, #0f2c4c 0%, #09192c 62%, #050d18 100%)',
      matBorderCss: 'rgba(56, 189, 248, 0.45)',
    },
    {
      id: 'u_cobalt',
      label: 'Tolarian Cobalt',
      accentHex: '#6366f1',
      matBgCss: 'radial-gradient(ellipse at center, #1c1f52 0%, #101230 62%, #08091a 100%)',
      matBorderCss: 'rgba(99, 102, 241, 0.45)',
    },
    {
      id: 'u_glacier',
      label: 'Glacier Cyan',
      accentHex: '#06b6d4',
      matBgCss: 'radial-gradient(ellipse at center, #0d3b44 0%, #072126 62%, #041114 100%)',
      matBorderCss: 'rgba(6, 182, 212, 0.45)',
    },
  ],
  B: [
    {
      id: 'b_amethyst',
      label: 'Liliana Amethyst',
      accentHex: '#a855f7',
      matBgCss: 'radial-gradient(ellipse at center, #2d1642 0%, #190c26 62%, #0d0614 100%)',
      matBorderCss: 'rgba(168, 85, 247, 0.45)',
    },
    {
      id: 'b_fuchsia',
      label: 'Phyrexian Orchid',
      accentHex: '#d946ef',
      matBgCss: 'radial-gradient(ellipse at center, #3b133d 0%, #210a22 62%, #120513 100%)',
      matBorderCss: 'rgba(217, 70, 239, 0.45)',
    },
    {
      id: 'b_slate',
      label: 'Urborg Obsidian',
      accentHex: '#94a3b8',
      matBgCss: 'radial-gradient(ellipse at center, #242936 0%, #141720 62%, #0a0c12 100%)',
      matBorderCss: 'rgba(148, 163, 184, 0.45)',
    },
  ],
  R: [
    {
      id: 'r_ember',
      label: 'Shivan Ember',
      accentHex: '#ef4444',
      matBgCss: 'radial-gradient(ellipse at center, #471519 0%, #290b0e 62%, #160507 100%)',
      matBorderCss: 'rgba(239, 68, 68, 0.45)',
    },
    {
      id: 'r_flame',
      label: 'Chandra Magma',
      accentHex: '#f97316',
      matBgCss: 'radial-gradient(ellipse at center, #4a220d 0%, #291206 62%, #170903 100%)',
      matBorderCss: 'rgba(249, 115, 22, 0.45)',
    },
    {
      id: 'r_crimson',
      label: 'Bloodcleave Rose',
      accentHex: '#f43f5e',
      matBgCss: 'radial-gradient(ellipse at center, #461226 0%, #260915 62%, #14040b 100%)',
      matBorderCss: 'rgba(244, 63, 94, 0.45)',
    },
  ],
  G: [
    {
      id: 'g_emerald',
      label: 'Llanowar Canopy',
      accentHex: '#10b981',
      matBgCss: 'radial-gradient(ellipse at center, #0f3b29 0%, #082217 62%, #04120c 100%)',
      matBorderCss: 'rgba(16, 185, 129, 0.45)',
    },
    {
      id: 'g_lime',
      label: 'Zendikar Moss',
      accentHex: '#84cc16',
      matBgCss: 'radial-gradient(ellipse at center, #253b10 0%, #152208 62%, #0b1204 100%)',
      matBorderCss: 'rgba(132, 204, 22, 0.45)',
    },
    {
      id: 'g_teal',
      label: 'Deepwood Jade',
      accentHex: '#14b8a6',
      matBgCss: 'radial-gradient(ellipse at center, #0e3834 0%, #07201e 62%, #041110 100%)',
      matBorderCss: 'rgba(20, 184, 166, 0.45)',
    },
  ],
  C: [
    {
      id: 'c_sand',
      label: 'Blind Eternities Sand',
      accentHex: '#d6a86c',
      matBgCss: 'radial-gradient(ellipse at center, #382c1e 0%, #201911 62%, #110d09 100%)',
      matBorderCss: 'rgba(214, 168, 108, 0.45)',
    },
    {
      id: 'c_steel',
      label: 'Darksteel Pewter',
      accentHex: '#cbd5e1',
      matBgCss: 'radial-gradient(ellipse at center, #262f3b 0%, #151b22 62%, #0b0e13 100%)',
      matBorderCss: 'rgba(203, 213, 225, 0.45)',
    },
  ],
};

/**
 * Fallback 8+ distinct tabletop variants so up to 8 seats are ALWAYS 100% visually distinct,
 * even when all 8 seats have no deck loaded or all 8 bring the exact same color identity!
 */
export const GLOBAL_DISTINCT_VARIANTS: SeatPaletteVariant[] = [
  IDENTITY_PALETTES.U[0],
  IDENTITY_PALETTES.R[0],
  IDENTITY_PALETTES.G[0],
  IDENTITY_PALETTES.W[0],
  IDENTITY_PALETTES.B[0],
  IDENTITY_PALETTES.R[1],
  IDENTITY_PALETTES.U[2],
  IDENTITY_PALETTES.B[1],
  IDENTITY_PALETTES.G[1],
  IDENTITY_PALETTES.C[0],
  IDENTITY_PALETTES.U[1],
  IDENTITY_PALETTES.R[2],
];

function extractColorsFromCard(card: CardDefinition): MtgColor[] {
  const found = new Set<MtgColor>();
  for (const c of card.colors ?? []) {
    if (WUBRG_ORDER.includes(c as MtgColor)) found.add(c as MtgColor);
  }
  const textToScan = `${card.manaCost ?? ''} ${card.oracleText ?? ''}`;
  for (const sym of WUBRG_ORDER) {
    if (textToScan.includes(`{${sym}}`)) found.add(sym);
  }
  return WUBRG_ORDER.filter((c) => found.has(c));
}

export function computeDeckColorIdentity(deck: ImportedDeckPayload): MtgColor[] {
  const found = new Set<MtgColor>();
  // If commanders exist, Commander color identity is strictly defined by the commander(s)
  if (deck.commanders.length > 0) {
    for (const cmd of deck.commanders) {
      for (const c of extractColorsFromCard(cmd)) found.add(c);
    }
  } else {
    for (const entry of deck.mainboard) {
      for (const c of extractColorsFromCard(entry.card)) found.add(c);
    }
  }
  return WUBRG_ORDER.filter((c) => found.has(c));
}

/**
 * Extracts official Scryfall card-art cosmetic options legal for this deck's color identity & format.
 * External image URLs are never generated or permitted.
 */
export function buildLegalCosmeticArtPool(
  deck: ImportedDeckPayload,
  _format?: FormatId
): CosmeticArtOption[] {
  const identity = computeDeckColorIdentity(deck);
  const identitySet = new Set<string>(identity);
  const seenNames = new Set<string>();
  const options: CosmeticArtOption[] = [];

  const pushCard = (card: CardDefinition, isCommander: boolean) => {
    const key = card.name.trim().toLowerCase();
    if (!key || seenNames.has(key)) return;
    // Enforce deck color-identity subset check when deck has commanders
    if (deck.commanders.length > 0) {
      const cardColors = extractColorsFromCard(card);
      if (cardColors.some((c) => !identitySet.has(c))) return;
    }
    seenNames.add(key);
    options.push({
      cardName: card.name,
      scryfallId: card.scryfallId,
      artCropUrl: scryfallArtCropUrl(card.name),
      colors: card.colors,
      isCommander,
    });
  };

  for (const cmd of deck.commanders) {
    pushCard(cmd, true);
  }
  for (const entry of deck.mainboard) {
    pushCard(entry.card, false);
    if (options.length >= 36) break;
  }

  // Also include color-identity-legal basic/iconic lands
  for (const land of IDENTITY_LAND_ART_POOL) {
    if (deck.commanders.length > 0 && land.colors.some((c) => !identitySet.has(c))) continue;
    const key = land.name.toLowerCase();
    if (!seenNames.has(key)) {
      seenNames.add(key);
      options.push({
        cardName: land.name,
        scryfallId: land.scryfallId,
        artCropUrl: scryfallArtCropUrl(land.name),
        colors: land.colors,
        isCommander: false,
      });
    }
  }

  return options;
}

/**
 * Candidate palette list for a given deck color identity (`[]` = Colorless, `['R']` = Mono-Red,
 * `['W','U','B','G']` = Atraxa, etc.). Multi-color decks prioritize each component color's palettes
 * in a deterministic rotation so two Atraxa or two Rakdos players get distinct component hues first!
 */
export function getCandidatePalettesForIdentity(
  colorIdentity: readonly string[],
  seatSeed = 0
): SeatPaletteVariant[] {
  if (colorIdentity.length === 0) {
    return [...IDENTITY_PALETTES.C, ...GLOBAL_DISTINCT_VARIANTS];
  }
  if (colorIdentity.length === 1) {
    const single = IDENTITY_PALETTES[colorIdentity[0]] ?? IDENTITY_PALETTES.C;
    return [...single, ...GLOBAL_DISTINCT_VARIANTS];
  }

  // Multi-color identity: interleave primary variants of each color in the deck's identity!
  // E.g. Two Rakdos (B/R) decks -> Player A gets Shivan Ember (#ef4444), Player B gets Liliana Amethyst (#a855f7)!
  const interleaved: SeatPaletteVariant[] = [];
  const rotatedColors = colorIdentity
    .slice(seatSeed % colorIdentity.length)
    .concat(colorIdentity.slice(0, seatSeed % colorIdentity.length));

  for (let tier = 0; tier < 3; tier++) {
    for (const c of rotatedColors) {
      const fam = IDENTITY_PALETTES[c];
      if (fam && fam[tier]) interleaved.push(fam[tier]);
    }
  }
  return [...interleaved, ...GLOBAL_DISTINCT_VARIANTS];
}

/**
 * Recomputes distinct `profileColor` and `playmatCss` across all seats in `seatOrder` so:
 * 1) Each player's color & default playmat reflects their deck's color identity when loaded;
 * 2) NO TWO PLAYERS at the table ever receive the same base color / mat variant, even if they
 *    play the exact same commander or same color identity!
 */
export function resolveDistinctTablePalettes(
  seats: Record<string, Pick<PlayerSeat, 'seatId' | 'seatIndex' | 'deckMeta'>>,
  seatOrder: readonly string[],
  tableSeed = 0
): Record<string, SeatPaletteVariant> {
  const usedIds = new Set<string>();
  const usedHexes = new Set<string>();
  const resolved: Record<string, SeatPaletteVariant> = {};

  // Seat players WITH loaded deck color identities first so they get their truest identity colors,
  // then assign remaining seats from unused distinct variants!
  const withDeck = seatOrder.filter((sid) => seats[sid]?.deckMeta);
  const withoutDeck = seatOrder.filter((sid) => !seats[sid]?.deckMeta);

  for (const sid of [...withDeck, ...withoutDeck]) {
    const s = seats[sid];
    if (!s) continue;
    const identity = s.deckMeta?.colorIdentity ?? [];
    const candidates = s.deckMeta
      ? getCandidatePalettesForIdentity(identity, s.seatIndex + tableSeed)
      : GLOBAL_DISTINCT_VARIANTS.slice((s.seatIndex + tableSeed) % GLOBAL_DISTINCT_VARIANTS.length).concat(
          GLOBAL_DISTINCT_VARIANTS.slice(0, (s.seatIndex + tableSeed) % GLOBAL_DISTINCT_VARIANTS.length)
        );

    const chosen =
      candidates.find((v) => !usedIds.has(v.id) && !usedHexes.has(v.accentHex)) ??
      GLOBAL_DISTINCT_VARIANTS[s.seatIndex % GLOBAL_DISTINCT_VARIANTS.length];

    usedIds.add(chosen.id);
    usedHexes.add(chosen.accentHex);
    resolved[sid] = chosen;
  }

  return resolved;
}

/**
 * Guards against any external URL injection: only Scryfall `art_crop` URLs built from a card
 * inside the player's `legalCosmeticArt` pool are ever permitted.
 */
export function resolveValidatedCardArtUrl(
  pool: readonly CosmeticArtOption[],
  cardName: string | null | undefined
): string | null {
  if (!cardName) return null;
  const target = cardName.trim().toLowerCase();
  const match = pool.find((opt) => opt.cardName.trim().toLowerCase() === target);
  return match ? match.artCropUrl : null;
}
